/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Link } from 'react-router';

import type { PatchedLocationDescriptor } from 'js/lib/connectToRouter';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2 } from '@coursera/cds-core';
import { SuccessIcon } from '@coursera/cds-icons';

import TooltipOverlay from 'bundles/grading/components/common/TooltipOverlay';
import type { GetUrlFn } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import type { ReviewPartProgress } from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';
import ProgressCircle from 'bundles/ui/components/ProgressCircle';

import _t from 'i18n!nls/grading';

type Props = {
  reviewPartProgress: ReviewPartProgress;
  hasUnsubmittedChanges: boolean;
  gradesSubmittedAtLeastOnce: boolean;
  currentSubmitterId: string;
  rubricMap: { [key: string]: { questionId: string } };
  getUrlCallback: GetUrlFn;
};

const styles = {
  container: css`
    margin-top: var(--cds-spacing-150);
    display: flex;

    .progress-icon {
      margin-right: var(--cds-spacing-100);

      & > .rc-ProgressCircle,
      & > svg {
        vertical-align: middle;
      }
    }
  `,
};

/**
 * Donut message
 */
const PartsToGradeMessage = ({ value, url }: { value: string; url: PatchedLocationDescriptor | null }) => {
  const message = (
    <FormattedMessage
      message={_t('{partsToGrade} {partsToGrade, plural, =1 {rubric} other {rubrics}} left to grade')}
      partsToGrade={`${value}`}
    />
  );
  if (url) {
    return (
      <TooltipOverlay id="nextUngradedRubric" text={_t('First ungraded rubric')}>
        <span>
          <Link to={url}>{message}</Link>
        </span>
      </TooltipOverlay>
    );
  }
  return message;
};

/**
 * Rubric Progress donut
 */
const RubricProgress = ({
  reviewPartProgress,
  hasUnsubmittedChanges = false,
  gradesSubmittedAtLeastOnce,
  currentSubmitterId,
  rubricMap,
  getUrlCallback,
}: Props) => {
  let progressIcon: React.ReactNode | undefined;

  const completedParts = reviewPartProgress.completedRequiredParts;
  const totalParts = reviewPartProgress.totalRequiredParts;

  if (totalParts === 0) {
    return null;
  }
  if (completedParts === totalParts && gradesSubmittedAtLeastOnce) {
    progressIcon = <SuccessIcon size="large" color="success" />;
  } else {
    progressIcon = (
      <ProgressCircle
        percentComplete={(completedParts / totalParts) * 100}
        arcColor={'var(--cds-color-green-700)'}
        backgroundColor={'var(--cds-color-grey-50)'}
        emptyColor={'var(--cds-color-grey-200)'}
        arcWidth={10}
        diameter={23}
      />
    );
  }

  const partsToGrade = totalParts - completedParts;
  const gradingFinished = partsToGrade === 0 && gradesSubmittedAtLeastOnce;

  const notGradedRubricId = Object.entries(reviewPartProgress.partDetails).find((entry) => {
    return entry[1].isRequired && !entry[1].isComplete;
  })?.[0];

  const url =
    notGradedRubricId && rubricMap[notGradedRubricId]
      ? getUrlCallback(currentSubmitterId, rubricMap[notGradedRubricId].questionId, notGradedRubricId)
      : null;

  return (
    <div css={styles.container} data-testid="grading-rubric-progress">
      <span className="progress-icon">{progressIcon}</span>
      <Typography2 variant="bodyPrimary" component="span">
        {(partsToGrade > 0 || !gradesSubmittedAtLeastOnce) && (
          <PartsToGradeMessage value={`${partsToGrade}`} url={url} />
        )}
        {gradingFinished && !hasUnsubmittedChanges && _t('Grading finished')}
        {gradingFinished && hasUnsubmittedChanges && _t('Grading finished, but there are  unsubmitted changes')}
      </Typography2>
    </div>
  );
};

export default RubricProgress;
